import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import FamilyLeaderboard from "@/components/FamilyLeaderboard";
import { DigestCard } from "@/components/DigestCard";
import FamilyHealth from "@/components/FamilyHealth";
import WatchRoom from "@/components/WatchRoom";
import { PaperPlaneRight, Image as ImageIcon, ShareNetwork, UsersThree, Copy, Smiley, Checks, Check, CaretLeft, CalendarBlank, ListChecks, Trash, FileArrowDown } from "@phosphor-icons/react";

const EMOJIS = ["😀", "😂", "❤️", "👍", "🎉", "😍", "😅", "🙏", "🔥", "😢", "😮", "👏", "🤔", "😴", "💪", "🏃", "🍕", "☕", "🎂", "✅"];
const REACTIONS = ["❤️", "👍", "😂", "🔥", "😮", "😢"];
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
const imgSrc = (img) => (img.startsWith("data:") || img.startsWith("http"))
  ? img : `${process.env.REACT_APP_BACKEND_URL}/api/chat/photo?path=${encodeURIComponent(img)}`;
const fmtDay = (iso) => new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });

function FamilySetup({ onDone }) {
  const [mode, setMode] = useState(null);
  const [value, setValue] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === "create") await api.post("/family", { name: value });
      else await api.post("/family/join", { code: value });
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="max-w-md mx-auto mt-16 glass-card p-8 text-center fade-in" data-testid="family-setup">
      <UsersThree size={40} weight="duotone" className="text-primary mx-auto mb-3" />
      <h2 className="font-heading text-xl font-bold mb-1">Семейный чат</h2>
      <p className="text-sm text-muted-foreground mb-6">Создайте семью или вступите по коду приглашения.</p>
      {!mode ? (
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={() => setMode("create")} data-testid="family-create-btn">Создать семью</Button>
          <Button variant="outline" onClick={() => setMode("join")} data-testid="family-join-btn">У меня есть код</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)} required
            placeholder={mode === "create" ? "Название — например «Наша семья»" : "Код приглашения (6 символов)"}
            data-testid="family-setup-input" />
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" data-testid="family-setup-submit">{mode === "create" ? "Создать" : "Вступить"}</Button>
            <Button type="button" variant="outline" onClick={() => setMode(null)}>Назад</Button>
          </div>
        </form>
      )}
    </div>
  );
}

function ShareDialog({ open, onClose, onShare }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (open) api.get("/chat/shareables").then((r) => setItems(r.data)).catch(() => {});
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass-modal max-h-[70vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Поделиться в чат</DialogTitle></DialogHeader>
        {items && (
          <div className="space-y-3">
            {items.events.length > 0 && <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">События</div>}
            {items.events.map((e, i) => (
              <button key={`e${i}`} onClick={() => onShare(e)} data-testid={`share-event-${i}`}
                className="w-full flex items-center gap-2 text-sm p-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left">
                <CalendarBlank size={15} className="text-[#4dabf5] shrink-0" />
                <span className="flex-1 truncate">{e.title}</span>
                <span className="text-xs text-muted-foreground num">{new Date(e.date).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}{e.time ? ` ${e.time}` : ""}</span>
              </button>
            ))}
            {items.tasks.length > 0 && <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Задачи</div>}
            {items.tasks.map((t, i) => (
              <button key={`t${i}`} onClick={() => onShare(t)} data-testid={`share-task-${i}`}
                className="w-full flex items-center gap-2 text-sm p-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left">
                <ListChecks size={15} className="text-[#00e676] shrink-0" />
                <span className="flex-1 truncate">{t.title}</span>
              </button>
            ))}
            {items.events.length === 0 && items.tasks.length === 0 && <p className="text-sm text-muted-foreground">Нечем делиться — нет предстоящих событий и открытых задач.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [family, setFamily] = useState(undefined);
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actionsFor, setActionsFor] = useState(null);
  const [view, setView] = useState("chats");
  const pressTimer = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const wsRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadFamily = useCallback(() => api.get("/family").then((r) => setFamily(r.data.family)).catch(() => setFamily(null)), []);
  const loadChannels = useCallback(() => api.get("/chat/channels").then((r) => setChannels(r.data)).catch(() => {}), []);

  useEffect(() => { loadFamily(); }, [loadFamily]);
  useEffect(() => { if (family) loadChannels(); }, [family, loadChannels]);

  useEffect(() => {
    if (!family) return;
    let alive = true;
    let poll;
    const connect = () => {
      const url = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/chat`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === "message") {
          const cur = activeRef.current;
          const matches = cur && (data.channel === cur.canon || data.channel === cur.channel);
          if (matches) {
            setMessages((m) => (m.some((x) => x.id === data.message.id) ? m : [...m, data.message]));
            if (data.message.sender_id !== user?.id) api.post("/chat/read", { channel: cur.channel }).then(() => window.dispatchEvent(new Event("chat-read"))).catch(() => {});
          }
          loadChannels();
          window.dispatchEvent(new Event("chat-read"));
        }
        if (data.type === "read") {
          const cur = activeRef.current;
          if (cur && (data.channel === cur.canon || data.channel === cur.channel)) {
            setMessages((m) => m.map((x) => (data.message_ids?.includes(x.id) ? { ...x, read_by_all: true } : x)));
          }
        }
      };
      ws.onclose = () => {
        if (!alive) return;
        poll = setTimeout(connect, 4000);
      };
    };
    connect();
    return () => { alive = false; clearTimeout(poll); wsRef.current?.close(); };
  }, [family, user?.id, loadChannels]);

  const openChannel = async (ch) => {
    setActive({ ...ch, canon: ch.kind === "family" ? "family" : dmCanon(ch.channel) });
    const { data } = await api.get(`/chat/messages?channel=${encodeURIComponent(ch.channel)}`);
    setMessages(data);
    await api.post("/chat/read", { channel: ch.channel }).catch(() => {});
    loadChannels();
    window.dispatchEvent(new Event("chat-read"));
  };

  const dmCanon = (channel) => {
    if (!channel.startsWith("dm:")) return "family";
    const other = channel.split(":")[1];
    const [a, b] = [user.id, other].sort();
    return `dm:${a}:${b}`;
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (payload) => {
    try {
      const { data } = await api.post("/chat/messages", { channel: active.channel, ...payload });
      const local = { id: data.id, sender_id: user.id, sender_name: user.name, text: payload.text || null,
        image: payload.image || null, share: payload.share || null, reactions: {}, deleted: false,
        created_at: data.created_at, read_by: [user.id], read_by_all: false };
      setMessages((m) => (m.some((x) => x.id === local.id) ? m : [...m, local]));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendText = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    await send({ text: t });
  };

  const react = async (mid, emoji) => {
    setActionsFor(null);
    try {
      const { data } = await api.post(`/chat/messages/${mid}/react`, { emoji });
      setMessages((m) => m.map((x) => (x.id === mid ? { ...x, reactions: data.reactions } : x)));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeMsg = async (mid) => {
    setActionsFor(null);
    try {
      await api.delete(`/chat/messages/${mid}`);
      setMessages((m) => m.map((x) => (x.id === mid ? { ...x, deleted: true, text: null, image: null, share: null, reactions: {} } : x)));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendPhoto = (file) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(async (blob) => {
        try {
          setUploading(true);
          const fd = new FormData();
          fd.append("file", blob, "photo.jpg");
          const { data } = await api.post("/chat/upload", fd);
          await send({ image: data.path });
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setUploading(false);
        }
      }, "image/jpeg", 0.72);
    };
    img.src = URL.createObjectURL(file);
  };

  if (family === undefined) return <div className="glass-card h-40 animate-pulse mt-10" />;
  if (family === null) return <FamilySetup onDone={loadFamily} />;

  return (
    <div className="fade-in h-full flex flex-col" data-testid="chat-page">
      {!active ? (
        <>
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{family.name}</p>
              <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Семья</h1>
            </div>
            {family.invite_code && (
              <button onClick={() => { navigator.clipboard.writeText(family.invite_code); toast.success("Код скопирован — отправьте его близким"); }}
                data-testid="invite-code-btn"
                className="flex items-center gap-2 px-3 py-2 rounded-lg glass-panel border border-white/[0.07] text-sm">
                Код: <span className="num font-bold text-primary">{family.invite_code}</span> <Copy size={14} className="text-muted-foreground" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mb-5" data-testid="family-tabs">
            {[["chats", "Чаты"], ["watch", "Смотреть вместе"], ["health", "Здоровье"], ["board", "Лидерборд"]].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} data-testid={`family-tab-${id}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          {view === "board" && (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {family.owner_id === user.id && (
                  <button data-testid="send-digest-btn"
                    onClick={async () => {
                      try { await api.post("/family/digest/send"); toast.success("Дайджест недели отправлен в семейный чат"); }
                      catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-lg glass-panel border border-amber-300/20 text-xs font-semibold text-amber-300 hover:bg-amber-300/10 transition-colors">
                    🏆 Прислать итоги недели в чат
                  </button>
                )}
                <button data-testid="family-wrapped-btn" onClick={() => navigate("/family-wrapped")}
                  className="flex items-center gap-2 px-3.5 py-2 rounded-lg glass-panel border border-[#4dabf5]/25 text-xs font-semibold text-[#4dabf5] hover:bg-[#4dabf5]/10 transition-colors">
                  ✨ Wrapped семьи {new Date().getFullYear()}
                </button>
              </div>
              <FamilyLeaderboard />
            </>
          )}
          {view === "watch" && <WatchRoom user={user} />}
          {view === "health" && <FamilyHealth family={family} meId={user.id} />}
          {view === "chats" && (
          <>
          <div className="space-y-2 stagger" data-testid="channels-list">
            {channels.map((ch) => (
              <button key={ch.channel} onClick={() => openChannel(ch)} data-testid={`channel-${ch.kind === "family" ? "family" : ch.channel}`}
                className="w-full glass-card p-4 flex items-center gap-3 text-left">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${ch.kind === "family" ? "bg-primary/15 text-primary" : "bg-[#a970ff]/15 text-[#a970ff]"}`}>
                  {ch.kind === "family" ? <UsersThree size={20} weight="duotone" /> : <span className="font-bold">{ch.label[0]}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{ch.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{ch.last_text || "Нет сообщений"}</div>
                </div>
                {ch.last_at && <span className="text-[10px] text-muted-foreground num shrink-0">{fmtTime(ch.last_at)}</span>}
                {ch.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-white text-[11px] num font-bold flex items-center justify-center" data-testid={`unread-${ch.channel}`}>{ch.unread}</span>}
              </button>
            ))}
          </div>
          {channels.length <= 1 && (
            <p className="text-xs text-muted-foreground mt-4">Пока вы одни — отправьте код приглашения близким, и здесь появятся личные чаты.</p>
          )}
          </>
          )}
        </>
      ) : (
        <div className="flex flex-col h-full min-h-0" data-testid="chat-window">
          <div className="flex items-center gap-2 pb-3 border-b border-white/[0.07] mb-2">
            <button onClick={() => { setActive(null); loadChannels(); }} data-testid="chat-back-btn" className="p-1.5 rounded-md hover:bg-secondary transition-colors"><CaretLeft size={18} /></button>
            <span className="font-heading font-bold">{active.label}</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0" data-testid="messages-list" onClick={() => actionsFor && setActionsFor(null)}>
            {messages.map((m, i) => {
              const own = m.sender_id === user.id;
              const isBot = m.sender_id === "bot:lifeos";
              const showDay = i === 0 || fmtDay(m.created_at) !== fmtDay(messages[i - 1].created_at);
              return (
                <div key={m.id}>
                  {showDay && <div className="text-center text-[10px] text-muted-foreground my-3 uppercase tracking-widest">{fmtDay(m.created_at)}</div>}
                  <div className={`flex ${own ? "justify-end" : "justify-start"}`}>
                    <div className={`group relative max-w-[80%] sm:max-w-[60%] rounded-2xl px-3.5 py-2 ${own ? "bg-primary/20 border border-primary/25" : isBot ? "glass-card !rounded-2xl !border-amber-300/20 w-full sm:!max-w-[440px]" : "glass-card !rounded-2xl"}`}
                      data-testid={`message-${m.id}`}
                      onContextMenu={(e) => { e.preventDefault(); if (!m.deleted) setActionsFor(actionsFor === m.id ? null : m.id); }}
                      onTouchStart={() => { if (!m.deleted) pressTimer.current = setTimeout(() => setActionsFor(m.id), 450); }}
                      onTouchEnd={() => clearTimeout(pressTimer.current)}
                      onTouchMove={() => clearTimeout(pressTimer.current)}>
                      {!m.deleted && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setActionsFor(actionsFor === m.id ? null : m.id); }}
                          data-testid={`msg-actions-btn-${m.id}`}
                          className={`absolute -top-2.5 ${own ? "-left-2.5" : "-right-2.5"} w-6 h-6 rounded-full glass-panel border border-white/[0.1] flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity z-10`}>
                          <Smiley size={13} />
                        </button>
                      )}
                      {actionsFor === m.id && (
                        <div className={`absolute -top-11 ${own ? "right-0" : "left-0"} flex items-center gap-1 px-2 py-1.5 rounded-full glass-panel border border-white/[0.12] shadow-xl z-20`}
                          data-testid={`msg-actions-${m.id}`} onClick={(e) => e.stopPropagation()}>
                          {REACTIONS.map((e) => (
                            <button key={e} type="button" onClick={() => react(m.id, e)} data-testid={`react-btn-${m.id}-${e}`}
                              className="text-lg hover:scale-125 transition-transform leading-none">{e}</button>
                          ))}
                          {own && (
                            <button type="button" onClick={() => removeMsg(m.id)} data-testid={`delete-msg-btn-${m.id}`}
                              className="ml-1 p-1 rounded-full text-muted-foreground hover:text-red-400 transition-colors"><Trash size={16} /></button>
                          )}
                        </div>
                      )}
                      {!own && active.kind === "family" && !isBot && <div className="text-[10px] font-semibold text-[#a970ff] mb-0.5">{m.sender_name}</div>}
                      {isBot && <div className="text-[10px] font-semibold text-amber-300 mb-1.5 flex items-center gap-1"><span>⚡</span>LifeOS</div>}
                      {m.deleted ? (
                        <div className="text-sm italic text-muted-foreground" data-testid={`message-deleted-${m.id}`}>Сообщение удалено</div>
                      ) : (
                        <>
                          {m.image && <img src={imgSrc(m.image)} alt="" className="rounded-lg max-h-64 mb-1" data-testid={`message-image-${m.id}`} />}
                          {m.file && (
                            <a href={imgSrc(m.file.path)} target="_blank" rel="noreferrer" download={m.file.name}
                              className="flex items-center gap-2 rounded-lg bg-white/[0.05] border border-white/[0.08] px-2.5 py-2 mb-1 hover:border-primary/50 transition-colors"
                              data-testid={`message-file-${m.id}`}>
                              <FileArrowDown size={18} className="text-primary shrink-0" />
                              <span className="text-xs font-medium truncate max-w-[200px]">{m.file.name}</span>
                              {m.file.size > 0 && <span className="text-[10px] text-muted-foreground num shrink-0">{m.file.size > 1048576 ? `${(m.file.size / 1048576).toFixed(1)} МБ` : `${Math.max(1, Math.round(m.file.size / 1024))} КБ`}</span>}
                            </a>
                          )}
                          {m.share && (
                            <div className="flex items-center gap-2 rounded-lg bg-white/[0.05] border border-white/[0.08] px-2.5 py-2 mb-1 text-xs" data-testid={`message-share-${m.id}`}>
                              {m.share.kind === "event" ? <CalendarBlank size={14} className="text-[#4dabf5]" /> : <ListChecks size={14} className="text-[#00e676]" />}
                              <span className="font-semibold">{m.share.title}</span>
                              {m.share.date && <span className="text-muted-foreground num">{new Date(m.share.date).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}{m.share.time ? ` ${m.share.time}` : ""}</span>}
                            </div>
                          )}
                          {m.digest && <DigestCard digest={m.digest} />}
                          {m.text && !m.digest && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                          {m.reactions && Object.keys(m.reactions).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(m.reactions).map(([e, ids]) => (
                                <button key={e} type="button" onClick={() => react(m.id, e)} data-testid={`reaction-${m.id}-${e}`}
                                  className={`px-1.5 py-0.5 rounded-full text-[11px] border transition-colors ${ids.includes(user.id) ? "bg-primary/25 border-primary/40" : "bg-white/[0.05] border-white/[0.08] hover:bg-white/[0.1]"}`}>
                                  {e} <span className="num font-semibold">{ids.length}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex items-center gap-1 justify-end mt-0.5">
                        <span className="text-[9px] text-muted-foreground num">{fmtTime(m.created_at)}</span>
                        {own && !m.deleted && (m.read_by_all ? <Checks size={12} className="text-primary" /> : <Check size={12} className="text-muted-foreground" />)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {messages.length === 0 && <p className="text-sm text-muted-foreground text-center mt-10">Напишите первое сообщение 👋</p>}
            <div ref={bottomRef} />
          </div>
          {showEmoji && (
            <div className="flex flex-wrap gap-1 p-2 glass-panel rounded-lg border border-white/[0.07] mb-1" data-testid="emoji-picker">
              {EMOJIS.map((e) => <button key={e} onClick={() => { setText((t) => t + e); }} className="text-xl hover:scale-125 transition-transform">{e}</button>)}
            </div>
          )}
          <form onSubmit={sendText} className="flex items-center gap-1.5 pt-2">
            <button type="button" onClick={() => setShowEmoji((s) => !s)} data-testid="emoji-btn" className="p-2 text-muted-foreground hover:text-[#ffea00] transition-colors"><Smiley size={20} /></button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-btn" className={`p-2 text-muted-foreground hover:text-primary transition-colors ${uploading ? "animate-pulse" : ""}`}><ImageIcon size={20} /></button>
            <button type="button" onClick={() => setShowShare(true)} data-testid="share-btn" className="p-2 text-muted-foreground hover:text-[#00e676] transition-colors"><ShareNetwork size={20} /></button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { if (e.target.files[0]) sendPhoto(e.target.files[0]); e.target.value = ""; }} data-testid="photo-input" />
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Сообщение…" className="flex-1" data-testid="chat-input" />
            <Button type="submit" size="icon" disabled={!text.trim()} data-testid="chat-send-btn"><PaperPlaneRight size={17} /></Button>
          </form>
          <ShareDialog open={showShare} onClose={() => setShowShare(false)}
            onShare={async (item) => { setShowShare(false); await send({ share: item }); }} />
        </div>
      )}
    </div>
  );
}
